import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { portalUrl } from '@command-center/portal-registry';
import { Dialog, DialogContent, cn } from '@command-center/ui-system';
import { Bot, Building2, ClipboardList, FileStack, FolderKanban, ListChecks, Plus, Search, Sparkles, Star, Upload, UsersRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { globalSearch, type SearchResult } from '../api';
import { display, recents, stars, useToast } from '../lib';

export interface PaletteContext {
  projectId?: string;
  projectName?: string;
  documentId?: string;
  openNewProject(mode?: 'manual' | 'siteplan'): void;
  openUpload(projectId?: string): void;
  openAlice(prompt?: string): void;
  openBrief(projectId: string): void;
  openMissingInfo(projectId: string): void;
}

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: React.ComponentType<{ className?: string }>;
  run(): void;
}

const typeIcons: Record<SearchResult['resourceType'], React.ComponentType<{ className?: string }>> = { project: FolderKanban, client: Building2, contact: UsersRound, document: FileStack };
const typeGroups: Record<SearchResult['resourceType'], string> = { project: 'Projects', client: 'Clients', contact: 'Contacts', document: 'Documents' };

/** Shared Command Center palette pattern (Ctrl/Cmd+K), configured for ECORP. */
export function CommandPalette({ open, onClose, context }: { open: boolean; onClose(): void; context: PaletteContext }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (open) { setQuery(''); setResults([]); setActive(0); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const timer = window.setTimeout(() => {
      void globalSearch(text).then(payload => setResults(payload.results)).catch(() => setResults([])).finally(() => setSearching(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const go = useCallback((url: string) => { onClose(); navigate(url); }, [navigate, onClose]);

  const items = useMemo<PaletteItem[]>(() => {
    const list: PaletteItem[] = [];
    for (const result of results) {
      list.push({ id: `${result.resourceType}:${result.recordId}`, label: result.title, hint: result.subtitle, group: typeGroups[result.resourceType], icon: typeIcons[result.resourceType], run: () => go(result.url) });
    }
    const actions: PaletteItem[] = [
      { id: 'new-project', label: 'New Project', hint: 'Manual or from a site plan', group: 'Actions', icon: Plus, run: () => { onClose(); context.openNewProject(); } },
      { id: 'new-project-siteplan', label: 'Start Project from Site Plan', group: 'Actions', icon: Sparkles, run: () => { onClose(); context.openNewProject('siteplan'); } },
      { id: 'upload', label: context.projectId ? `Upload Document to ${context.projectName ?? 'This Project'}` : 'Upload Document', group: 'Actions', icon: Upload, run: () => { onClose(); context.openUpload(context.projectId); } },
      { id: 'alice', label: context.projectId ? 'Ask Alice About This Project' : 'Open Alice', group: 'Actions', icon: Bot, run: () => { onClose(); context.openAlice(context.projectId ? `Summarize the current project.` : undefined); } },
      { id: 'projects', label: 'Search Projects', group: 'Navigate', icon: FolderKanban, run: () => go('/projects') },
      { id: 'clients', label: 'Search Clients', group: 'Navigate', icon: Building2, run: () => go('/clients') },
      { id: 'contacts', label: 'Search Contacts', group: 'Navigate', icon: UsersRound, run: () => go('/contacts') },
      { id: 'documents', label: 'Search Documents', group: 'Navigate', icon: FileStack, run: () => go('/documents') },
      { id: 'missing', label: 'View Projects Missing Information', group: 'Navigate', icon: ListChecks, run: () => go('/projects?filterField=Project%20Health&filterValue=Attention%20Needed') },
    ];
    if (context.projectId) {
      actions.push(
        { id: 'brief', label: 'Generate Project Brief', group: 'This Project', icon: ClipboardList, run: () => { onClose(); context.openBrief(context.projectId!); } },
        { id: 'missing-info', label: 'Find Missing Information', group: 'This Project', icon: ListChecks, run: () => { onClose(); context.openMissingInfo(context.projectId!); } },
        { id: 'copy-link', label: 'Copy Project Link', group: 'This Project', icon: FolderKanban, run: () => { void navigator.clipboard.writeText(portalUrl('ecorp', `/projects/${context.projectId}`)).then(() => toast('Project link copied.')); onClose(); } },
        { id: 'star', label: stars.has(context.projectId) ? 'Remove Project from Quick Access' : 'Pin Project to Quick Access', group: 'This Project', icon: Star, run: () => { const pinned = stars.toggle(context.projectId!, context.projectName ?? context.projectId!); toast(pinned ? 'Project pinned to Quick Access.' : 'Project removed from Quick Access.'); onClose(); } },
      );
    }
    const recentItems: PaletteItem[] = query.trim().length < 2
      ? recents.list().slice(0, 5).map(item => ({ id: `recent:${item.id}`, label: item.label, hint: 'Recent project', group: 'Recent', icon: FolderKanban, run: () => go(`/projects/${item.id}`) }))
      : [];
    const text = query.trim().toLowerCase();
    const filteredActions = text ? actions.filter(action => action.label.toLowerCase().includes(text)) : actions;
    return [...list, ...recentItems, ...filteredActions];
  }, [results, context, query, go, onClose, toast]);

  useEffect(() => { setActive(0); }, [items.length, query]);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive(current => Math.min(current + 1, items.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(current => Math.max(current - 1, 0)); }
    else if (event.key === 'Enter') { event.preventDefault(); items[active]?.run(); }
  }
  useEffect(() => { listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const groups = useMemo(() => { const map = new Map<string, Array<{ item: PaletteItem; index: number }>>(); items.forEach((item, index) => { const list = map.get(item.group) ?? []; list.push({ item, index }); map.set(item.group, list); }); return [...map.entries()]; }, [items]);

  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="top-[15%] max-w-xl translate-y-0 gap-0 overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground"/>
          <input ref={inputRef} className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" placeholder="Search projects, clients, contacts, documents, or type a command…" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onKeyDown} aria-label="Command palette search"/>
          <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">ESC</kbd>
        </div>
        <div ref={listRef} className="max-h-[55vh] overflow-y-auto p-2">
          {searching && <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>}
          {!searching && query.trim().length >= 2 && !results.length && <p className="px-3 py-2 text-sm text-muted-foreground">No records match “{query.trim()}”.</p>}
          {groups.map(([group, entries]) => (
            <div key={group} className="mb-1">
              <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
              {entries.map(({ item, index }) => (
                <button key={item.id} data-index={index} type="button" className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm', index === active ? 'bg-primary/10 text-foreground' : 'hover:bg-muted')} onMouseEnter={() => setActive(index)} onClick={() => item.run()}>
                  <item.icon className="h-4 w-4 shrink-0 text-muted-foreground"/>
                  <span className="flex-1 truncate">{display(item.label)}</span>
                  {item.hint && <span className="max-w-[45%] truncate text-xs text-muted-foreground">{item.hint}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
